import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { trackLeadSubmission } from "../utils/conversionTracking";
import { submitLead } from "../utils/leadSubmission";
import "./ProductPage.css";

const emptyForm = {
  name: "",
  phone: "",
  email: "",
};

const ProductDetail = ({ product }) => {
  const [selectedImage, setSelectedImage] = useState(product.images[0]);
  const [showModal, setShowModal] = useState(false);
  const [quantity, setQuantity] = useState(product.quantities[0]);
  const [formData, setFormData] = useState(emptyForm);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData(emptyForm);
    setError("");
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      const lead = await submitLead({
        type: "product",
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        product: product.title,
        quantity,
        price: product.price,
      });

      setShowModal(false);
      trackLeadSubmission({ source: "product-modal", type: lead.type });
      navigate("/thankyou", { state: lead });
    } catch {
      setError("We could not submit your request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="product-page container py-5">
      <div className="row">
        <div className="col-md-6">
          <div className="product-gallery">
            <img
              src={selectedImage}
              alt={product.title}
              className="product-main-image img-fluid rounded"
            />
            <div className="product-thumbnails d-flex mt-3">
              {product.images.map((image, index) => (
                <button
                  type="button"
                  key={image}
                  className="p-0 border-0 bg-transparent me-2"
                  onClick={() => setSelectedImage(image)}
                  aria-label={`View ${product.title} image ${index + 1}`}
                >
                  <img
                    src={image}
                    alt=""
                    className={`img-thumbnail ${
                      selectedImage === image ? "active" : ""
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <h1 className="product-title">{product.title}</h1>
          <p className="text-muted">Brand: {product.brand}</p>
          <div className="rating mb-3">
            <span>{product.rating}</span>{" "}
            <small>({product.reviewCount} Reviews)</small>
          </div>

          <div className="product-price mt-3">
            <span className="text-danger fs-3">
              ${product.price.toFixed(2)}
            </span>
            {product.originalPrice && (
              <span className="original-price ms-2">
                ${product.originalPrice.toFixed(2)}
              </span>
            )}
            {product.savingsLabel && (
              <span className="text-success ms-3 fs-5">
                {product.savingsLabel}
              </span>
            )}
          </div>

          <p className="mt-4 text-success">In Stock</p>

          <div className="product-quantity d-flex align-items-center mb-4">
            <label htmlFor={`quantity-${product.title}`} className="me-2">
              Quantity:
            </label>
            <select
              id={`quantity-${product.title}`}
              className="form-select w-auto"
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value))}
            >
              {product.quantities.map((q) => (
                <option key={q} value={q}>
                  {q}
                </option>
              ))}
            </select>
          </div>

          <div className="buy-buttons d-flex gap-3">
            <button
              type="button"
              className="btn btn-warning"
              onClick={() => setShowModal(true)}
            >
              Add to Cart
            </button>
            <button
              type="button"
              className="btn btn-blue"
              onClick={() => setShowModal(true)}
            >
              Buy Now
            </button>
          </div>

          <ul className="product-features mt-5">
            {product.features.map((feature) => (
              <li
                key={feature.text}
                className="d-flex align-items-center mb-3"
              >
                <i
                  className={`${feature.icon} me-3 ${feature.color} fa-2x`}
                ></i>
                <span>{feature.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="product-description mt-5 p-4 bg-light rounded shadow-sm">
        <h3>Product Description</h3>
        <p>{product.description}</p>
      </div>

      <div className="product-reviews mt-5 p-4 bg-white rounded shadow-sm">
        <h3>Customer Reviews</h3>
        {product.reviews.map((review) => (
          <div
            key={`${review.name}-${review.quote}`}
            className="review-item mb-4 border-bottom pb-3"
          >
            <p>
              <strong>{review.name}</strong> -{" "}
              <span className="text-warning">{review.rating}</span>
            </p>
            <p>"{review.quote}"</p>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content p-4">
            <h2>Enter Your Details</h2>
            <form onSubmit={handleFormSubmit}>
              <div className="mb-3">
                <label className="form-label" htmlFor="product-name">
                  Full Name
                </label>
                <input
                  id="product-name"
                  type="text"
                  name="name"
                  className="form-control"
                  required
                  value={formData.name}
                  onChange={handleFormChange}
                />
              </div>
              <div className="mb-3">
                <label className="form-label" htmlFor="product-phone">
                  Phone
                </label>
                <input
                  id="product-phone"
                  type="tel"
                  name="phone"
                  className="form-control"
                  required
                  value={formData.phone}
                  onChange={handleFormChange}
                />
              </div>
              <div className="mb-3">
                <label className="form-label" htmlFor="product-email">
                  Email
                </label>
                <input
                  id="product-email"
                  type="email"
                  name="email"
                  className="form-control"
                  required
                  value={formData.email}
                  onChange={handleFormChange}
                />
              </div>

              {error && <p className="text-danger">{error}</p>}

              <button
                type="submit"
                className="btn btn-success w-100"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </button>
              <button
                type="button"
                className="btn btn-secondary w-100 mt-2"
                onClick={handleCloseModal}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default ProductDetail;
